/**
 * API Client and Auth utilities
 * Separated from App.js to avoid circular dependencies
 */

import { createContext, useContext } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context - will be provided by App.js
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

/**
 * Extract error message from API error response
 * Handles both string and object error details (Pydantic validation errors)
 * Prevents React error #31 (objects as React children)
 */
export const getErrorMessage = (error, fallback = "An error occurred") => {
  if (!error) return fallback;
  
  const detail = error.response?.data?.detail;
  
  if (!detail) {
    return error.message || fallback;
  }
  
  // If detail is a string, return it
  if (typeof detail === 'string') {
    return detail;
  }
  
  // If detail is an array (Pydantic validation errors)
  if (Array.isArray(detail)) {
    const firstError = detail[0];
    if (firstError?.msg) {
      const field = firstError.loc?.join(' -> ') || '';
      return field ? `${field}: ${firstError.msg}` : firstError.msg;
    }
    return fallback;
  }
  
  // If detail is an object with msg property
  if (typeof detail === 'object' && detail.msg) {
    return detail.msg;
  }
  
  // If detail is an object with message property
  if (typeof detail === 'object' && detail.message) {
    return detail.message;
  }
  
  // Last resort: stringify (but avoid for rendering)
  try {
    return JSON.stringify(detail);
  } catch {
    return fallback;
  }
};

// API helper with auth
export const apiClient = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" }
});

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem("viraly_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // Don't redirect for admin API calls - let them handle their own auth/fallback
    const isAdminEndpoint = error.config?.url?.includes('/admin/');
    
    if (error.response?.status === 401 && !isAdminEndpoint) {
      localStorage.removeItem("viraly_token");
      localStorage.removeItem("viraly_user");
      window.location.href = "/auth";
    }
    return Promise.reject(error);
  }
);
